<template>
  <CModal
    :show="show"
    @update:show="$emit('update:show', $event)"
    size="xl"
    centered
    :close-on-backdrop="false"
  >
    <template #header>
      <div class="modal-header-inner">
        <div class="modal-header-avatar">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor"/>
          </svg>
        </div>
        <h5 class="modal-title-main">Testar &amp; Treinar IA</h5>
        <span class="modal-title-sep">·</span>
        <p class="modal-title-sub">Simule pedidos e corrija mapeamentos do dicionário</p>
      </div>
      <button
        type="button"
        class="modal-close-btn"
        @click="$emit('update:show', false)"
      >
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    </template>

    <div class="chat-container">
      <!-- Contexto: cliente + filial -->
      <div class="context-bar">
        <div class="context-field context-field--cliente">
          <label>Cliente</label>
          <div class="search-wrapper" ref="searchWrapper">
            <input
              type="text"
              class="context-input"
              v-model="clienteSearch"
              placeholder="Buscar por nome, CNPJ ou código..."
              @input="onClienteSearchInput"
              @focus="showClienteDropdown = clienteResults.length > 0"
            />
            <div
              v-if="showClienteDropdown && clienteResults.length"
              class="search-dropdown"
            >
              <div
                v-for="c in clienteResults"
                :key="c.id"
                class="search-dropdown-item"
                @mousedown.prevent="selectCliente(c)"
              >
                <span class="dropdown-cliente-nome">{{ c.fant || c.razSoc }}</span>
                <span class="dropdown-cliente-cnpj">{{ c.cpfCnpj }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="context-field">
          <label>Filial</label>
          <select
            class="context-input context-select"
            v-model="selectedFilialId"
            :disabled="!filiais.length"
            @change="onFilialChange"
          >
            <option value="">{{ selectedCliente ? 'Selecione...' : 'Selecione um cliente' }}</option>
            <option
              v-for="f in filiais"
              :key="f.filial_id"
              :value="f.filial_id"
            >
              {{ f.fantasia || f.razaosocial || `Filial ${f.filial_id}` }}
              <template v-if="f.cidade"> - {{ f.cidade }}/{{ f.uf }}</template>
            </option>
          </select>
        </div>

        <div class="context-field context-field--tabela">
          <label>Tabela</label>
          <input
            type="text"
            class="context-input context-input--readonly"
            v-model="selectedTabelaPreco"
            placeholder="Auto"
            readonly
          />
        </div>

        <!-- Status indicator -->
        <div class="context-status">
          <span
            class="status-dot"
            :class="contextReady ? 'status-dot--ready' : 'status-dot--idle'"
          ></span>
          <span class="status-label">{{ contextReady ? 'Pronto' : 'Configure o contexto' }}</span>
        </div>
      </div>

      <!-- Chat area -->
      <div class="chat-area" ref="chatArea">
        <!-- Estado vazio: como usar -->
        <div v-if="!messages.length" class="how-to-use">
          <div class="how-to-use-glow"></div>
          <div class="how-to-use-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor"/>
            </svg>
          </div>
          <h3 class="how-to-use-title">Pronto para testar</h3>
          <p class="how-to-use-sub">Simule pedidos como um vendedor faria e ensine a IA a entender o vocabulário da sua equipe</p>
          <div class="tips-grid">
            <div class="tip-card">
              <div class="tip-card-icon">
                <CIcon name="cil-pencil" size="sm" />
              </div>
              <div>
                <strong>Escreva naturalmente</strong>
                <p>"10 barra de cano 100, 5 luva esgoto"</p>
              </div>
            </div>
            <div class="tip-card">
              <div class="tip-card-icon">
                <CIcon name="cil-list" size="sm" />
              </div>
              <div>
                <strong>Listas de itens</strong>
                <p>Com quantidade, código ou descrição</p>
              </div>
            </div>
            <div class="tip-card">
              <div class="tip-card-icon">
                <CIcon name="cil-file" size="sm" />
              </div>
              <div>
                <strong>Arquivos e áudios</strong>
                <p>JPG, PDF, MP3, OGG e mais</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Mensagens -->
        <div
          v-for="(msg, mi) in messages"
          :key="mi"
          class="chat-msg"
        >
          <!-- Mensagem do usuario -->
          <div v-if="msg.type === 'user'" class="msg-user">
            <div class="msg-user-content">
              <div class="msg-user-bubble">
                <div v-if="msg.text" class="msg-text">{{ msg.text }}</div>
                <div v-if="msg.files && msg.files.length" class="msg-files">
                  <span v-for="(f, fi) in msg.files" :key="fi" class="file-chip">
                    <CIcon name="cil-file" size="sm" />
                    <span class="file-chip-name">{{ f }}</span>
                  </span>
                </div>
              </div>
            </div>
            <div class="msg-user-avatar">
              <CIcon name="cil-user" />
            </div>
          </div>

          <!-- Resposta da IA -->
          <div v-if="msg.type === 'ai'" class="msg-ai">
            <div class="msg-ai-avatar">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor"/>
              </svg>
            </div>
            <div class="msg-ai-content">
              <div class="msg-ai-label">IA</div>

              <!-- Loading dots -->
              <div v-if="msg.loading" class="loading-card">
                <div class="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span class="loading-text">Processando pedido...</span>
              </div>

              <!-- Erro -->
              <div v-else-if="msg.error" class="error-card">
                <div class="error-card-icon">
                  <CIcon name="cil-warning" size="sm" />
                </div>
                <span>{{ msg.error }}</span>
              </div>

              <!-- Sugestoes -->
              <template v-else>
                <div v-if="!msg.sugestoes.length" class="empty-result">
                  Nenhuma sugestão retornada.
                </div>

                <div
                  v-for="(sug, si) in msg.sugestoes"
                  :key="si"
                  class="sugestao-card"
                >
                  <!-- Header -->
                  <div class="sugestao-header">
                    <div class="sugestao-index">{{ si + 1 }}</div>
                    <span class="sugestao-label">{{ sug.descricaoLimpa }}</span>
                  </div>

                  <!-- Produtos encontrados -->
                  <div v-if="hasProdutos(sug)" class="sugestao-produtos">
                    <div
                      v-for="(prod, pi) in getProdutosFlat(sug)"
                      :key="pi"
                      class="produto-row"
                    >
                      <div class="produto-thumb">
                        <img
                          :src="produtoImageUrl(prod)"
                          :alt="prod.descComp"
                          @error="$event.target.style.display='none'"
                        />
                      </div>
                      <div class="produto-info">
                        <span class="produto-nome">{{ prod.descComp }}</span>
                        <span class="produto-cod">#{{ prod.idExibicao || prod.id }}</span>
                      </div>
                      <span v-if="prod.qtdSugerida" class="produto-qtd">
                        {{ prod.qtdSugerida }}{{ prod.und ? ` ${prod.und}` : '' }}
                      </span>
                    </div>
                  </div>
                  <div v-else class="sugestao-sem-produto">
                    {{ sug.motivo || 'Nenhum produto encontrado para este item.' }}
                  </div>

                  <!-- Motivo -->
                  <div v-if="sug.motivo && hasProdutos(sug)" class="sugestao-motivo">
                    {{ sug.motivo }}
                  </div>

                  <!-- Hint da IA -->
                  <div v-if="sug._correction.hasAiHint" class="sugestao-hint">
                    <div class="hint-icon-wrap">
                      <CIcon name="cil-lightbulb" size="sm" />
                    </div>
                    <span>
                      A IA detectou:
                      <strong>"{{ sug.correcao_termo }}"</strong>
                      &rarr;
                      <strong>"{{ sug.correcao_sugestao }}"</strong>
                    </span>
                  </div>

                  <!-- Campos de correcao -->
                  <div v-if="!sug._correction.ignored" class="correction-section">
                    <div class="correction-section-label">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      Treinar mapeamento
                    </div>
                    <div class="correction-row">
                      <div class="correction-field">
                        <label>Como a IA procurou</label>
                        <input
                          type="text"
                          class="correction-input"
                          v-model="sug._correction.termo"
                          placeholder="Termo usado na busca"
                          @input="buscarSemelhantes(sug)"
                        />
                      </div>
                      <div class="correction-arrow">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </div>
                      <div class="correction-field">
                        <label>Como deveria procurar</label>
                        <input
                          type="text"
                          class="correction-input"
                          v-model="sug._correction.canonico"
                          placeholder="Termo correto"
                          @input="buscarSemelhantes(sug)"
                        />
                      </div>
                      <div class="correction-actions">
                        <button
                          v-if="!sug._correction.saved"
                          class="btn-save-correction"
                          :disabled="!sug._correction.termo || !sug._correction.canonico"
                          @click="saveCorrection(msg, si)"
                        >
                          <CIcon name="cil-save" size="sm" />
                          Salvar
                        </button>
                        <span v-else class="correction-saved">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          Salvo
                        </span>
                        <button
                          v-if="!sug._correction.saved"
                          class="btn-ignore-correction"
                          @click="ignoreCorrection(sug)"
                        >
                          Ignorar
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Semelhantes encontrados -->
                  <div
                    v-if="sug._correction.semelhantes.length && !sug._correction.saved && !sug._correction.ignored"
                    class="semelhantes-hint"
                  >
                    <div class="semelhantes-title">
                      <CIcon name="cil-info" size="sm" />
                      <span>Termos semelhantes já cadastrados</span>
                    </div>
                    <div
                      v-for="(s, idx) in sug._correction.semelhantes"
                      :key="idx"
                      class="semelhante-item"
                    >
                      <span class="semelhante-texto">"{{ s.termo }}" &rarr; "{{ s.termo_canonico }}"</span>
                      <span
                        class="semelhante-badge"
                        :class="s.match_em === 'alias' ? 'badge-alias' : 'badge-canonico'"
                      >
                        {{ s.match_em === 'alias' ? 'alias' : 'canônico' }}
                      </span>
                      <span class="semelhante-pct">{{ Math.round(s.similaridade * 100) }}%</span>
                    </div>
                  </div>

                  <div v-else-if="sug._correction.ignored" class="correction-ignored">
                    <em>Correção ignorada.</em>
                    <a href="#" @click.prevent="unignoreCorrection(sug)">Desfazer</a>
                  </div>

                  <!-- Aviso de especificidade -->
                  <div
                    v-if="isGenericToSpecific(sug._correction)"
                    class="specificity-warning"
                  >
                    <CIcon name="cil-warning" size="sm" class="warn-icon" />
                    <span>
                      Mapear "<strong>{{ sug._correction.termo }}</strong>" para algo
                      muito específico pode restringir buscas futuras.
                    </span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Prompt container -->
      <div class="prompt-container" :class="{ focused: promptFocused }">
        <!-- Arquivos selecionados -->
        <div v-if="selectedFiles.length" class="files-preview">
          <div
            v-for="(f, fi) in selectedFiles"
            :key="fi"
            class="file-chip"
          >
            <CIcon name="cil-file" size="sm" />
            <span class="file-chip-name">{{ f.name }}</span>
            <button type="button" class="file-chip-remove" @click="removeFile(fi)">
              <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Textarea -->
        <textarea
          class="prompt-textarea"
          v-model="promptText"
          placeholder="Descreva o pedido como o vendedor faria..."
          rows="2"
          @keydown.enter.exact="onEnterKey"
          @focus="promptFocused = true"
          @blur="promptFocused = false"
        ></textarea>

        <!-- Acoes -->
        <div class="prompt-actions">
          <label class="upload-btn" title="Anexar arquivos">
            <CIcon name="cil-paperclip" size="sm" />
            <span>Anexar</span>
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.gif,.mp3,.wav,.ogg,.opus,.mp4,.m4a"
              @change="onFilesSelected"
              ref="fileInput"
              style="display:none"
            />
          </label>

          <div class="prompt-hint">
            <kbd>Enter</kbd> para enviar · <kbd>Shift+Enter</kbd> para nova linha
          </div>

          <div class="prompt-actions-right">
            <button
              v-if="lastPrompt"
              class="revalidate-btn"
              :disabled="sending"
              @click="revalidate"
              title="Re-enviar o último prompt para verificar se as correções melhoraram"
            >
              <CIcon name="cil-reload" size="sm" />
              Revalidar
            </button>
            <button
              class="submit-btn"
              :disabled="sending || (!promptText.trim() && !selectedFiles.length) || !contextReady"
              @click="sendMessage"
            >
              <template v-if="sending">
                <span class="btn-spinner"></span>
                Pensando...
              </template>
              <template v-else>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Enviar
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>

    <template #footer><span></span></template>
  </CModal>
</template>

<script>
import { get, post } from "@/helpers/apiRequest";
import { cdnUrlProduto } from "@/helpers/url";

let clienteSearchTimeout = null;
let semelhantesTimeout = null;

export default {
  name: "TreinoChatIA",
  props: {
    show: { type: Boolean, default: false },
  },
  data() {
    return {
      // Contexto
      clienteSearch: "",
      clienteResults: [],
      showClienteDropdown: false,
      selectedCliente: null,
      selectedFilialId: "",
      selectedTabelaPreco: "",
      filiais: [],

      // Chat
      messages: [],
      promptText: "",
      promptFocused: false,
      selectedFiles: [],
      sending: false,

      // Revalidar
      lastPrompt: "",
      lastFiles: [],
    };
  },
  computed: {
    contextReady() {
      return (
        this.selectedCliente &&
        this.selectedFilialId &&
        this.selectedTabelaPreco
      );
    },
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
  },
  methods: {
    // --- Busca de cliente ---
    onClienteSearchInput() {
      clearTimeout(clienteSearchTimeout);
      this.selectedCliente = null;
      this.filiais = [];
      this.selectedFilialId = "";
      this.selectedTabelaPreco = "";

      if (this.clienteSearch.length < 2) {
        this.clienteResults = [];
        this.showClienteDropdown = false;
        return;
      }
      clienteSearchTimeout = setTimeout(() => this.searchClientes(), 400);
    },

    async searchClientes() {
      try {
        const q = encodeURIComponent(this.clienteSearch);
        const resp = await get(`/admin/clientes?q=${q}&take=10`);
        this.clienteResults = resp.itens || resp.data || [];
        this.showClienteDropdown = this.clienteResults.length > 0;
      } catch (e) {
        console.error("Erro ao buscar clientes:", e);
        this.clienteResults = [];
        this.showClienteDropdown = false;
      }
    },

    async selectCliente(cliente) {
      this.selectedCliente = cliente;
      this.clienteSearch = cliente.fant || cliente.razSoc || `#${cliente.id}`;
      this.clienteResults = [];
      this.showClienteDropdown = false;

      await this.loadFiliaisCliente(cliente.id);
    },

    onDocumentClick(e) {
      if (this.$refs.searchWrapper && !this.$refs.searchWrapper.contains(e.target)) {
        this.showClienteDropdown = false;
      }
    },

    // --- Filiais do cliente ---
    async loadFiliaisCliente(clienteId) {
      this.filiais = [];
      this.selectedFilialId = "";
      this.selectedTabelaPreco = "";

      try {
        const resp = await get(`/admin/dicionario-ia/clientes/${clienteId}/filiais`);
        this.filiais = resp.data || resp || [];

        if (this.filiais.length === 1) {
          this.selectedFilialId = this.filiais[0].filial_id;
          this.selectedTabelaPreco = this.filiais[0].tabelapreco || "";
        }
      } catch (e) {
        console.error("Erro ao carregar filiais:", e);
      }
    },

    onFilialChange() {
      const filial = this.filiais.find(
        (f) => String(f.filial_id) === String(this.selectedFilialId)
      );
      this.selectedTabelaPreco = filial ? filial.tabelapreco || "" : "";
    },

    // --- Arquivos ---
    onFilesSelected(e) {
      const files = Array.from(e.target.files || []);
      this.selectedFiles = [...this.selectedFiles, ...files];
      this.$refs.fileInput.value = "";
    },

    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },

    // --- Envio ---
    onEnterKey(e) {
      if (!e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },

    async sendMessage() {
      const prompt = this.promptText.trim();
      if (!prompt && !this.selectedFiles.length) return;

      if (!this.contextReady) {
        return;
      }

      const userMsg = {
        type: "user",
        text: prompt,
        files: this.selectedFiles.map((f) => f.name),
      };
      this.messages.push(userMsg);

      this.lastPrompt = prompt;
      this.lastFiles = [...this.selectedFiles];

      const aiMsg = {
        type: "ai",
        loading: true,
        error: null,
        sugestoes: [],
      };
      this.messages.push(aiMsg);

      const filesToSend = [...this.selectedFiles];
      this.promptText = "";
      this.selectedFiles = [];
      this.sending = true;

      this.scrollToBottom();

      try {
        const url = `/admin/sugestoes/pedido-ia/${this.selectedCliente.id}/${this.selectedFilialId}/${this.selectedTabelaPreco}`;
        let resp;

        if (filesToSend.length) {
          const formData = new FormData();
          if (prompt) formData.append("prompt", prompt);
          filesToSend.forEach((f) => formData.append("arquivo[]", f));
          resp = await post(url, formData, { isFile: true });
        } else {
          resp = await post(url, { prompt });
        }

        const data = resp.data || resp;
        const itens = data.itens
          ? data.itens.data || data.itens
          : [];

        aiMsg.sugestoes = itens.map((item) => this.enrichSugestao(item));
        aiMsg.loading = false;
      } catch (e) {
        aiMsg.loading = false;
        aiMsg.error = e.message || "Erro ao processar sugestão.";
      }

      this.sending = false;
      this.$forceUpdate();
      this.scrollToBottom();
    },

    enrichSugestao(sug) {
      const termoBusca = sug.termo_busca || "";
      const correcaoTermo = sug.correcao_termo || "";
      const correcaoSugestao = sug.correcao_sugestao || "";

      let descricaoLimpa = sug.descricao || "";
      descricaoLimpa = descricaoLimpa.replace(/^\d+\s*x\s*/i, "").trim();

      return {
        ...sug,
        descricaoLimpa,
        _correction: {
          termo: correcaoTermo || termoBusca,
          canonico: correcaoSugestao,
          saved: false,
          ignored: false,
          hasAiHint: !!(correcaoTermo && correcaoSugestao),
          semelhantes: [],
          buscandoSemelhantes: false,
        },
      };
    },

    hasProdutos(sug) {
      return Array.isArray(sug.produtos) && sug.produtos.some((p) => p.id);
    },

    getProdutosFlat(sug) {
      if (!Array.isArray(sug.produtos)) return [];

      return sug.produtos
        .filter((p) => p.id)
        .map((p) => ({
          id: p.id,
          idExibicao: p.idExibicao || p.id,
          idImagem: p.idImagem || p.id,
          descComp: p.descComp || `Produto #${p.id}`,
          qtdSugerida: p.qtdSugerida,
          und: p.und || '',
          emb: p.emb,
          codBarra: p.codBarra,
        }));
    },

    produtoImageUrl(prod) {
      return cdnUrlProduto(prod.idImagem || prod.idExibicao || prod.id);
    },

    // --- Semelhantes ---
    buscarSemelhantes(sug) {
      clearTimeout(semelhantesTimeout);
      const c = sug._correction;
      const termo = (c.termo || "").trim();
      const canonico = (c.canonico || "").trim();

      if (termo.length < 3 && canonico.length < 3) {
        this.$set(c, "semelhantes", []);
        return;
      }

      this.$set(c, "buscandoSemelhantes", true);
      semelhantesTimeout = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (termo.length >= 3) params.set("termo", termo);
          if (canonico.length >= 3) params.set("canonico", canonico);

          const resp = await get(`/admin/dicionario-ia/semelhantes?${params.toString()}`);
          const data = resp.data || resp || [];
          this.$set(c, "semelhantes", data);
        } catch (e) {
          console.error("Erro ao buscar semelhantes:", e);
          this.$set(c, "semelhantes", []);
        } finally {
          this.$set(c, "buscandoSemelhantes", false);
        }
      }, 500);
    },

    // --- Correcoes ---
    ignoreCorrection(sug) {
      this.$set(sug._correction, "ignored", true);
    },

    unignoreCorrection(sug) {
      this.$set(sug._correction, "ignored", false);
    },

    async saveCorrection(msg, sugestaoIndex) {
      const sug = msg.sugestoes[sugestaoIndex];
      const c = sug._correction;
      if (!c.termo || !c.canonico) return;

      try {
        await post("/admin/dicionario-ia/treino/salvar-lote", {
          termos: [
            {
              termo: c.termo.toLowerCase().trim(),
              termo_canonico: c.canonico.toLowerCase().trim(),
              tipo: "produto",
            },
          ],
        });
        this.$set(c, "saved", true);
      } catch (e) {
        alert("Erro ao salvar correção: " + (e.message || e));
      }
    },

    // --- Revalidar ---
    async revalidate() {
      if (!this.lastPrompt) return;

      if (this.lastFiles.length) {
        const proceed = confirm(
          "Os arquivos do envio anterior não podem ser reenviados. " +
            "Apenas o texto do prompt será revalidado. Continuar?"
        );
        if (!proceed) return;
      }

      this.promptText = this.lastPrompt;
      this.selectedFiles = [];
      await this.sendMessage();
    },

    // --- Aviso de especificidade ---
    isGenericToSpecific(correction) {
      if (!correction.termo || !correction.canonico) return false;
      if (correction.saved || correction.ignored) return false;

      const termoWords = correction.termo.trim().split(/\s+/);
      const hasNumbers = /\d/.test(correction.termo);
      if (termoWords.length > 2 || hasNumbers) return false;

      const canonicoWords = correction.canonico.trim().split(/\s+/);
      const dimensionalPattern =
        /\b(\d+\s*mm|\d+\s*cm|\d+\s*m\b|\d+\s*l\b|\d+\s*kg|\d+\s*un|\d+\s*ml)\b/i;
      const hasDimensional = dimensionalPattern.test(correction.canonico);

      return canonicoWords.length >= 3 || hasDimensional;
    },

    // --- Utilitarios ---
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatArea;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
/* ==========================================
   VARIÁVEIS
   ========================================== */
.chat-container {
  --primary: #3a3e81;
  --primary-light: #5a5e9e;
  --primary-bg: #f2f2f8;
  --primary-border: #dddce9;
  --accent: #2298a3;
  --accent-bg: #edf8f9;
  --accent-border: #c5e6e9;
  --surface: #ffffff;
  --surface-2: #f8fafc;
  --border: #e5e7eb;
  --border-light: #f3f4f6;
  --text: #111827;
  --text-2: #374151;
  --text-3: #6b7280;
  --text-4: #9ca3af;
  --success: #16a34a;
  --success-bg: #f0fdf4;
  --warning: #ea580c;
  --warning-bg: #fff7ed;
  --warning-border: #fed7aa;
  --error: #dc2626;
  --error-bg: #fef2f2;
  --error-border: #fecaca;
  --radius-sm: 6px;
  --radius: 10px;
  --radius-lg: 14px;

  display: flex;
  flex-direction: column;
  min-height: 500px;
  max-height: 72vh;
}

/* ==========================================
   MODAL HEADER
   ========================================== */
::v-deep .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 16px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  min-height: unset;
}

.modal-header-inner {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.modal-header-avatar {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 7px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.modal-title-main {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
}

.modal-title-sep {
  color: var(--text-4);
  font-size: 13px;
  flex-shrink: 0;
}

.modal-title-sub {
  margin: 0;
  font-size: 12px;
  color: var(--text-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-close-btn {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  padding: 0;
  margin-left: 8px;
}

.modal-close-btn:hover {
  background: var(--surface-2);
  border-color: var(--text-4);
  color: var(--text);
}

/* ==========================================
   CONTEXT BAR
   ========================================== */
.context-bar {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 14px;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border);
}

.context-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 140px;
}

.context-field--cliente {
  flex: 1;
}

.context-field--tabela {
  min-width: 80px;
  max-width: 100px;
}

.context-field label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--text-3);
}

.context-input {
  height: 30px;
  padding: 0 9px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 13px;
  color: var(--text);
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
  width: 100%;
}

.context-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(58, 62, 129, 0.08);
}

.context-input--readonly {
  background: var(--surface-2);
  color: var(--text-3);
  cursor: default;
}

.context-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;
}

.search-wrapper {
  position: relative;
}

.search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  max-height: 220px;
  overflow-y: auto;
  z-index: 1050;
}

.search-dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  cursor: pointer;
  transition: background 0.1s;
  border-bottom: 1px solid var(--border-light);
}

.search-dropdown-item:last-child {
  border-bottom: none;
}

.search-dropdown-item:hover {
  background: var(--primary-bg);
}

.dropdown-cliente-nome {
  font-size: 13px;
  font-weight: 500;
  color: var(--text);
}

.dropdown-cliente-cnpj {
  font-size: 11px;
  color: var(--text-4);
}

/* Context status */
.context-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 5px;
  flex-shrink: 0;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot--ready {
  background: var(--success);
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.2);
  animation: pulse-green 2s infinite;
}

.status-dot--idle {
  background: var(--text-4);
}

@keyframes pulse-green {
  0%, 100% { box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.2); }
  50% { box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.1); }
}

.status-label {
  font-size: 11px;
  color: var(--text-3);
  white-space: nowrap;
}

/* ==========================================
   CHAT AREA
   ========================================== */
.chat-area {
  flex: 1;
  overflow-y: auto;
  padding: 24px 20px;
  background: var(--surface);
}

/* Scrollbar */
.chat-area::-webkit-scrollbar {
  width: 4px;
}
.chat-area::-webkit-scrollbar-track {
  background: transparent;
}
.chat-area::-webkit-scrollbar-thumb {
  background: var(--border);
  border-radius: 4px;
}

/* ==========================================
   ESTADO VAZIO
   ========================================== */
.how-to-use {
  max-width: 560px;
  margin: 40px auto 0;
  text-align: center;
  position: relative;
}

.how-to-use-glow {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 80px;
  background: radial-gradient(ellipse, rgba(58, 62, 129, 0.12) 0%, transparent 70%);
  pointer-events: none;
}

.how-to-use-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
  box-shadow: 0 4px 16px rgba(58, 62, 129, 0.3);
}

.how-to-use-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 6px;
}

.how-to-use-sub {
  font-size: 13px;
  color: var(--text-3);
  line-height: 1.5;
  margin: 0 0 20px;
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  text-align: left;
}

.tip-card {
  display: flex;
  gap: 10px;
  padding: 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.tip-card-icon {
  width: 28px;
  height: 28px;
  min-width: 28px;
  background: var(--primary-bg);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
}

.tip-card strong {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 3px;
}

.tip-card p {
  margin: 0;
  font-size: 11px;
  color: var(--text-3);
  line-height: 1.4;
}

/* ==========================================
   MENSAGENS
   ========================================== */
.chat-msg {
  margin-bottom: 20px;
}

/* Usuário - alinhado à direita */
.msg-user {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.msg-user-avatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px;
  background: var(--primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.msg-user-content {
  max-width: 70%;
}

.msg-user-bubble {
  background: linear-gradient(135deg, var(--primary) 0%, #4c51a3 100%);
  border-radius: 14px 2px 14px 14px;
  padding: 11px 15px;
}

.msg-user-bubble .msg-text {
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
  color: #fff;
}

.msg-files {
  margin-top: 8px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* IA - alinhado à esquerda */
.msg-ai {
  display: flex;
  gap: 10px;
}

.msg-ai-avatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--accent) 0%, #1a7a84 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.msg-ai-content {
  flex: 1;
  min-width: 0;
}

.msg-ai-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--accent);
  margin-bottom: 6px;
}

/* Loading */
.loading-card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 2px 14px 14px 14px;
}

.loading-dots {
  display: flex;
  gap: 4px;
  align-items: center;
}

.loading-dots span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
  animation: dot-bounce 1.2s ease-in-out infinite;
}

.loading-dots span:nth-child(2) { animation-delay: 0.2s; }
.loading-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes dot-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-5px); opacity: 1; }
}

.loading-text {
  font-size: 13px;
  color: var(--text-3);
}

/* Erro */
.error-card {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--error);
  background: var(--error-bg);
  border: 1px solid var(--error-border);
  border-radius: 2px 14px 14px 14px;
  padding: 11px 15px;
  font-size: 13px;
}

.error-card-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.empty-result {
  color: var(--text-4);
  font-size: 13px;
  font-style: italic;
  padding: 8px 0;
}

/* ==========================================
   SUGESTÃO CARDS
   ========================================== */
.sugestao-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 2px 12px 12px 12px;
  overflow: hidden;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s;
}

.sugestao-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.sugestao-card:last-child {
  margin-bottom: 0;
}

.sugestao-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px 10px;
  border-bottom: 1px solid var(--border-light);
}

.sugestao-index {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 6px;
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  color: var(--accent);
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sugestao-label {
  font-weight: 600;
  font-size: 13px;
  color: var(--text);
}

.sugestao-produtos {
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.produto-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: var(--surface-2);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-sm);
  transition: border-color 0.1s;
}

.produto-row:hover {
  border-color: var(--border);
}

.produto-thumb {
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
}

.produto-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.produto-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.produto-nome {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.produto-cod {
  color: var(--text-4);
  font-size: 11px;
}

.produto-qtd {
  background: var(--primary);
  color: #fff;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.sugestao-sem-produto {
  padding: 10px 14px;
  color: var(--text-4);
  font-size: 13px;
  font-style: italic;
}

.sugestao-motivo {
  padding: 0 14px 10px;
  font-size: 12px;
  color: var(--text-3);
  line-height: 1.5;
}

.sugestao-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: var(--accent);
  background: var(--accent-bg);
  border-top: 1px solid var(--accent-border);
  padding: 9px 14px;
  line-height: 1.4;
}

.hint-icon-wrap {
  flex-shrink: 0;
  margin-top: 1px;
}

/* ==========================================
   SEÇÃO DE CORREÇÃO
   ========================================== */
.correction-section {
  padding: 10px 14px 12px;
  border-top: 1px solid var(--border-light);
  background: var(--surface-2);
}

.correction-section-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--text-3);
  margin-bottom: 8px;
}

.correction-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.correction-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.correction-field label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--text-3);
}

.correction-input {
  height: 32px;
  padding: 0 9px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 12px;
  color: var(--text);
  outline: none;
  width: 100%;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.correction-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.08);
}

.correction-arrow {
  color: var(--text-4);
  padding-bottom: 7px;
  flex-shrink: 0;
}

.correction-actions {
  display: flex;
  gap: 4px;
  align-items: center;
  padding-bottom: 2px;
  flex-shrink: 0;
}

.btn-save-correction {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-save-correction:hover:not(:disabled) {
  background: var(--primary-light);
}

.btn-save-correction:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-ignore-correction {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  background: transparent;
  color: var(--text-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-ignore-correction:hover {
  background: var(--surface-2);
  color: var(--text);
}

.correction-saved {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--success);
  font-size: 12px;
  font-weight: 600;
}

.correction-ignored {
  padding: 8px 14px;
  font-size: 12px;
  color: var(--text-4);
  border-top: 1px solid var(--border-light);
}

.correction-ignored a {
  margin-left: 6px;
  color: var(--primary);
  text-decoration: none;
}

.correction-ignored a:hover {
  text-decoration: underline;
}

/* ==========================================
   SEMELHANTES
   ========================================== */
.semelhantes-hint {
  margin: 0;
  background: var(--primary-bg);
  border-top: 1px solid var(--primary-border);
  padding: 10px 14px;
  font-size: 12px;
}

.semelhantes-title {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--primary);
  font-weight: 600;
  margin-bottom: 7px;
}

.semelhante-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  color: var(--text-2);
  line-height: 1.4;
  border-bottom: 1px dashed var(--primary-border);
}

.semelhante-item:last-child {
  border-bottom: none;
}

.semelhante-texto {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.semelhante-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.badge-alias {
  background: var(--accent-bg);
  color: var(--accent);
  border: 1px solid var(--accent-border);
}

.badge-canonico {
  background: var(--primary-bg);
  color: var(--primary);
  border: 1px solid var(--primary-border);
}

.semelhante-pct {
  color: var(--text-4);
  font-size: 11px;
  flex-shrink: 0;
}

/* Aviso especificidade */
.specificity-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: var(--warning);
  background: var(--warning-bg);
  border-top: 1px solid var(--warning-border);
  padding: 9px 14px;
  line-height: 1.4;
}

.warn-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

/* ==========================================
   PROMPT CONTAINER
   ========================================== */
.prompt-container {
  background: var(--surface);
  border: 1.5px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 12px 14px;
  margin: 0 16px 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.prompt-container.focused {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(58, 62, 129, 0.08);
}

.prompt-textarea {
  width: 100%;
  min-height: 48px;
  padding: 0;
  border: none;
  font-size: 14px;
  line-height: 1.6;
  resize: none;
  outline: none;
  color: var(--text);
  background: transparent;
  font-family: inherit;
}

.prompt-textarea::placeholder {
  color: var(--text-4);
}

.files-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-bottom: 10px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--border-light);
}

/* file chip (também usado nas mensagens) */
.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 20px;
  font-size: 12px;
  color: var(--text-2);
}

.file-chip-name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-chip-remove {
  background: transparent;
  border: none;
  color: var(--text-4);
  display: flex;
  align-items: center;
  padding: 0 1px;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.15s;
}

.file-chip-remove:hover {
  background: rgba(239, 68, 68, 0.1);
  color: var(--error);
}

.prompt-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border-light);
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  color: var(--text-3);
  font-size: 12px;
  font-weight: 500;
  transition: all 0.15s;
  margin: 0;
}

.upload-btn:hover {
  background: var(--surface-2);
  color: var(--text-2);
  border-color: var(--text-4);
}

.prompt-hint {
  font-size: 11px;
  color: var(--text-4);
  flex: 1;
  text-align: center;
}

.prompt-hint kbd {
  display: inline-block;
  padding: 1px 5px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 4px;
  font-size: 10px;
  font-family: inherit;
  color: var(--text-3);
}

.prompt-actions-right {
  display: flex;
  gap: 6px;
  align-items: center;
}

.revalidate-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  background: transparent;
  color: var(--primary);
  border: 1px solid var(--primary-border);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.15s;
}

.revalidate-btn:hover:not(:disabled) {
  background: var(--primary-bg);
  border-color: var(--primary);
}

.revalidate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 18px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.2s;
  box-shadow: 0 1px 4px rgba(58, 62, 129, 0.2);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(58, 62, 129, 0.3);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-spinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Esconder footer do modal */
::v-deep .modal-footer {
  display: none !important;
}
</style>
